import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import FilterListIcon from "@mui/icons-material/FilterList";
import { toast } from "react-toastify";
import { getEventTypes, formatApiDate, locationFilterParams } from "../../api/superAdminApi";
import { useLocationCascading } from "./hooks/useLocationCascading";
import { useAdminPanel } from "./hooks/useAdminPanel";
import SuperAdminLocationSelects from "./SuperAdminLocationSelects";
import SuperAdminPageHeader from "./SuperAdminPageHeader";

export default function VMSuperAdminEventTypes() {
  const { adminScope, locationLocked } = useAdminPanel();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [cards, setCards] = useState([]);
  const [totalEnquiries, setTotalEnquiries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const loc = useLocationCascading();

  const buildParams = useCallback(() => {
    const params = { ...locationFilterParams(loc, adminScope, { locked: locationLocked }) };
    const from = formatApiDate(fromDate);
    const to = formatApiDate(toDate);
    if (from) params.from = from;
    if (to) params.to = to;
    return params;
  }, [fromDate, toDate, loc, adminScope, locationLocked]);

  const fetchEventTypes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getEventTypes(buildParams());
      const payload = res.data?.data || {};
      setCards(Array.isArray(payload.items) ? payload.items : []);
      setTotalEnquiries(payload.total ?? 0);
    } catch {
      setCards([]);
      setTotalEnquiries(0);
      toast.info("Event types API unavailable.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchEventTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setFiltersOpen(false);
    fetchEventTypes();
  };

  return (
    <Box>
      <SuperAdminPageHeader
        title="Event Types"
        actions={
          <Button
            variant={filtersOpen ? "contained" : "outlined"}
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen((o) => !o)}
          >
            Filters
          </Button>
        }
      />

      {filtersOpen ? (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack spacing={2.5}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker label="From Date" value={fromDate} onChange={setFromDate} format="DD/MM/YYYY" slotProps={{ textField: { size: "small", fullWidth: true } }} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <DatePicker label="To Date" value={toDate} onChange={setToDate} format="DD/MM/YYYY" minDate={fromDate?.isValid() ? fromDate : undefined} slotProps={{ textField: { size: "small", fullWidth: true } }} />
              </Grid>
            </Grid>
            <Divider />
            <Grid container spacing={2}>
              <SuperAdminLocationSelects {...loc} locationLocked={locationLocked} lockedScope={adminScope} menuMinWidth={300} itemProps={{ xs: 12, sm: 6, md: 4 }} />
            </Grid>
            <Button variant="contained" onClick={handleSearch} disabled={loading} sx={{ alignSelf: "flex-start" }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : "Search"}
            </Button>
          </Stack>
        </Paper>
      ) : null}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Total events (filtered): {loading ? "…" : totalEnquiries}
      </Typography>

      <Grid container spacing={2}>
        {loading && cards.length === 0 ? (
          <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress aria-label="Loading event types" />
          </Grid>
        ) : cards.length === 0 ? (
          <Grid item xs={12}>
            <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
              No event type data for the current filters.
            </Typography>
          </Grid>
        ) : (
          cards.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.event_type}>
              <Card elevation={0} sx={{ bgcolor: "primary.main", color: "primary.contrastText", borderRadius: 2 }}>
                <CardContent sx={{ textAlign: "center", py: 2.5 }}>
                  <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500 }}>
                    {item.event_type}
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
                    {item.count ?? 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
}
